import { useEffect } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { fetchTodoNoteWorkspace } from '../../services/todoNoteService';
import type { TodoNoteWorkspaceSnapshot } from '../../utils/todoNoteWorkspaceCache';
import {
  isTodoNoteWorkspaceCacheStorageKey,
  loadTodoNoteWorkspaceCache,
} from '../../utils/todoNoteWorkspaceCache';
import { consumeTodoNoteLoginHeadSyncPending } from '../../utils/todoNoteLoginHeadSync';
import { setTodoNoteWorkspaceQueryData } from '../noteCacheUpdates';
import { noteKeys } from '../keys';
import { queryClient } from '../queryClient';
import { lsInitialDataOptions } from './lsInitialData';

export type UseTodoNoteWorkspaceQueryParams = {
  userId: string;
  noteWorkspaceId: string | null;
  enabled?: boolean;
  forceRefresh?: boolean;
};

export function useTodoNoteWorkspaceQuery(
  params: UseTodoNoteWorkspaceQueryParams
): UseQueryResult<TodoNoteWorkspaceSnapshot> {
  const { userId, noteWorkspaceId, enabled = true, forceRefresh = false } = params;
  const ls = loadTodoNoteWorkspaceCache(userId, noteWorkspaceId);

  // 다른 탭에서 Todo 저장 시 LS → RQ 패치 (fetch 없음)
  useEffect(() => {
    if (!userId || !enabled) return undefined;
    const onStorage = (e: StorageEvent) => {
      if (!isTodoNoteWorkspaceCacheStorageKey(e.key, userId, noteWorkspaceId)) return;
      const next = loadTodoNoteWorkspaceCache(userId, noteWorkspaceId);
      if (!next.found) return;
      setTodoNoteWorkspaceQueryData(userId, noteWorkspaceId, next.data);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [userId, noteWorkspaceId, enabled]);

  // 로그인·새로고침 직후 플래그가 있을 때만 revision head 비교(필요 시 풀 로드)
  useEffect(() => {
    if (!enabled || !userId) return;
    if (!consumeTodoNoteLoginHeadSyncPending()) return;
    void queryClient.fetchQuery({
      queryKey: noteKeys.todoWorkspace(userId, noteWorkspaceId),
      queryFn: () => fetchTodoNoteWorkspace(noteWorkspaceId, { forceRefresh: false }),
    });
  }, [enabled, userId, noteWorkspaceId]);

  return useQuery<TodoNoteWorkspaceSnapshot>({
    queryKey: noteKeys.todoWorkspace(userId, noteWorkspaceId),
    enabled: enabled && !!userId,
    ...lsInitialDataOptions(ls.found, ls.data, ls.savedAt),
    queryFn: () => fetchTodoNoteWorkspace(noteWorkspaceId, { forceRefresh }),
  });
}
